import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EcosystemAPI, ENTITY_TYPES, DOMAIN_LABELS, CATEGORY_LABELS, ENHANCED_DOMAINS, ENHANCED_CATEGORIES, mapServerError } from "../../lib/ecosystemApi";
import { EnhancedChip } from "../../components/ecosystem/Chips";

export default function NewEntity() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    entity_type: "organization",
    canonical_name: "",
    public_description: "",
    service_area: "",
    domain_keys: [],
    category_keys: [],
  });
  const [err, setErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const enhancedPreview = useMemo(() => {
    const dom = form.domain_keys.filter((d) => ENHANCED_DOMAINS.has(d));
    const cat = form.category_keys.filter((c) => ENHANCED_CATEGORIES.has(c));
    if (!dom.length && !cat.length) return null;
    return {
      enhanced_review: true,
      enhanced_domain_triggers: dom,
      enhanced_category_triggers: cat,
    };
  }, [form.domain_keys, form.category_keys]);

  const toggle = (k, v) => {
    setForm((f) => {
      const has = f[k].includes(v);
      return { ...f, [k]: has ? f[k].filter((x) => x !== v) : [...f[k], v] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setSubmitting(true);
    try {
      const created = await EcosystemAPI.create({
        entity_type: form.entity_type,
        canonical_name: form.canonical_name.trim(),
        public_description: form.public_description.trim() || null,
        service_area: form.service_area.trim() || null,
        domain_keys: form.domain_keys,
        category_keys: form.category_keys,
      });
      nav(`/ecosystem/entities/${created.id}`);
    } catch (e) {
      setErr(mapServerError(e));
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-2xl space-y-4" data-testid="ecosystem-new-entity-form">
      <h1 className="text-2xl font-semibold">New entity</h1>
      <p className="text-sm text-slate-500">Creates the entity as a DRAFT. It becomes patient-visible only after review, approval, and publish.</p>

      <div>
        <label className="text-sm font-medium block mb-1">Entity type</label>
        <select data-testid="new-entity-type" className="border rounded p-2 text-sm w-full" value={form.entity_type} onChange={(e) => setForm({ ...form, entity_type: e.target.value })}>
          {ENTITY_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
      </div>

      <div>
        <label className="text-sm font-medium block mb-1">Canonical name (required)</label>
        <input data-testid="new-entity-name" className="border rounded p-2 text-sm w-full" value={form.canonical_name} onChange={(e) => setForm({ ...form, canonical_name: e.target.value })} maxLength={200} required />
      </div>

      <div>
        <label className="text-sm font-medium block mb-1">Public description</label>
        <textarea data-testid="new-entity-description" className="border rounded p-2 text-sm w-full" rows={3} maxLength={4000} value={form.public_description} onChange={(e) => setForm({ ...form, public_description: e.target.value })} />
      </div>

      <div>
        <label className="text-sm font-medium block mb-1">Domains</label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(DOMAIN_LABELS).map(([k, v]) => (
            <label key={k} className="text-sm flex items-center gap-1 border rounded px-2 py-1">
              <input data-testid={`new-entity-domain-${k}`} type="checkbox" checked={form.domain_keys.includes(k)} onChange={() => toggle("domain_keys", k)} /> {v}
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="text-sm font-medium block mb-1">Categories</label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
            <label key={k} className="text-sm flex items-center gap-1 border rounded px-2 py-1">
              <input data-testid={`new-entity-category-${k}`} type="checkbox" checked={form.category_keys.includes(k)} onChange={() => toggle("category_keys", k)} /> {v}
            </label>
          ))}
        </div>
      </div>

      {enhancedPreview && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded" data-testid="new-entity-enhanced-preview">
          <EnhancedChip classification={enhancedPreview} />
          <div className="text-xs text-slate-600 mt-1">This entity will be classified as enhanced-review; approval requires an enhanced reviewer.</div>
        </div>
      )}

      {err && <div className="text-sm text-red-700" data-testid="new-entity-error">{err}</div>}

      <div className="flex gap-2">
        <button type="submit" disabled={submitting || !form.canonical_name.trim()} data-testid="new-entity-submit" className="px-4 py-2 text-sm rounded bg-slate-900 text-white disabled:bg-slate-400">
          Create draft
        </button>
        <button type="button" onClick={() => nav(-1)} className="px-4 py-2 text-sm rounded border">Cancel</button>
      </div>
    </form>
  );
}
